import { BranchesOutlined, EyeOutlined, ProfileOutlined } from '@ant-design/icons';
import { Button, Popover, Tag, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type WorkflowStatus, type WorkflowStep, type WorkflowTemplate } from '@/api/workflows';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { StatusTag } from '@/components/StatusTag';
import {
  CARD,
  CARD_BODY,
  CARD_HEADER,
  CARD_HEADER_TITLE,
  COLS,
  FIELD_BLOCK,
  FIELD_LABEL,
  FIELD_VALUE,
  INLINE_LABEL,
  INLINE_ROW,
  OUTER_WRAP,
  emptyValue,
} from '@/styles/workflowCardStyles';

import { AdditionalDocumentEntriesDrawer } from './AdditionalDocumentEntriesDrawer';
import { collectStepConditions } from './conditionSummary';
import { OneSpanConfigDrawer } from './OneSpanConfigDrawer';
import { PdfEntriesDrawer } from './PdfEntriesDrawer';
import { metaFor, stepTypeLabel } from './stepTypes';

const { Text } = Typography;

/** Role-scoped base for the per-step runtime-preview route. Both segments exist
 *  as routes today (`/{role}/workflows/:id/preview`). */
export type PreviewBase = '/approver/workflows' | '/originator/workflows';

type Props = {
  isFr: boolean;
  /** Details-card fields — sourced from the live template (review) or a frozen
   *  version snapshot (version details). The body is purely presentational and
   *  doesn't know which. */
  name: string;
  description: string;
  assignee: string;
  platform: string;
  /** Optional version label row (e.g. "v1.0"). Hidden when absent. */
  versionLabel?: string | null;
  status: WorkflowStatus;
  /** Drives the Published/Archived split on the status badge. */
  isActive?: boolean;
  /**
   * Render the status badge with decision-verb semantics ("Approved" /
   * "Rejected") instead of workflow-state labels ("Published"). Set by the
   * Approver's review page so an approved workflow reads as "Approved" from the
   * reviewer's perspective, not the Originator's "Published".
   */
  statusDecisionMode?: boolean;
  /** Rendered inline (red block) only when present. */
  rejectionComment?: string | null;
  /** Steps to render on the right — current template steps (review) or a
   *  version's frozen snapshot. */
  steps: WorkflowStep[];
  /** Template context for condition-label resolution + the PDF drawer's
   *  templateId/allSteps. For a version view, pass the template with its
   *  `steps` swapped for the frozen snapshot so labels resolve consistently. */
  template: WorkflowTemplate;
  /** Version whose frozen steps are shown — threaded into the per-step preview
   *  URL so preview shows THIS version's content, not the current state. Null
   *  falls back to the plain (current-state) preview route. */
  versionNumber: number | null;
  previewBase: PreviewBase;
  /** Dealer-code gating lists — always sourced from the LIVE template, never
   *  frozen per-version (the domain doesn't version them). Hidden entirely
   *  when empty. When `isFrozenSnapshot` is set, a caveat note clarifies these
   *  reflect current settings rather than this historical version's. */
  includedDealerCodes?: string[];
  excludedDealerCodes?: string[];
  /** True only when `steps`/`name`/`description` are a frozen historical
   *  version snapshot (VersionDetailsPage), not the live template (review
   *  pages). Note `versionNumber` is non-null in BOTH cases — it's used for
   *  the per-step preview URL, not as a snapshot signal — so this is a
   *  separate flag rather than reusing it. Drives the dealer-codes caveat. */
  isFrozenSnapshot?: boolean;
  /** Optional handler that intercepts the top-right "Preview" button click.
   *  When provided (approver review page), it opens the in-place preview
   *  modal instead of navigating away. When absent (originator version
   *  details), the button navigates to the standalone /preview route. */
  onPreview?: () => void;
};

/**
 * Read-only "Workflow Details + Steps" body shared by the Approver's
 * <WorkflowReview> (wrapped in its approve/reject bar) and the role-agnostic
 * <VersionDetailsPage> (fed a frozen version snapshot). Holds no role or
 * decision logic — everything is driven by props — so the same rendering
 * serves the live pending version and any historical version.
 */
export function WorkflowReviewBody({
  isFr,
  name,
  description,
  assignee,
  platform,
  versionLabel,
  status,
  isActive,
  statusDecisionMode,
  rejectionComment,
  steps,
  template,
  versionNumber,
  previewBase,
  includedDealerCodes,
  excludedDealerCodes,
  isFrozenSnapshot,
  onPreview,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notProvided = t('workflow.details.notProvided');
  // PdfFileUpload preview — read-only drawer showing the configured PDFs for
  // the clicked step. Null = closed.
  const [pdfPreviewStep, setPdfPreviewStep] = useState<WorkflowStep | null>(null);
  // OneSpanESign / AdditionalDocuments "View configuration" — read-only drawers
  // reusing the originator config drawers with editable=false. Null = closed.
  const [oneSpanConfigStep, setOneSpanConfigStep] = useState<WorkflowStep | null>(null);
  const [docsConfigStep, setDocsConfigStep] = useState<WorkflowStep | null>(null);
  // "View configuration" — for OneSpanESign / AdditionalDocuments steps it opens
  // the matching read-only drawer; for section-based steps it navigates to the
  // read-only builder-style config page, pinned to the version being reviewed.
  const handleViewConfig = (step: WorkflowStep) => {
    if (step.type === 'OneSpanESign') {
      setOneSpanConfigStep(step);
      return;
    }
    if (step.type === 'AdditionalDocuments') {
      setDocsConfigStep(step);
      return;
    }
    const versionQuery = versionNumber !== null ? `?version=${versionNumber}` : '';
    navigate(`${previewBase}/${template.id}/steps/${step.id}/config${versionQuery}`);
  };

  return (
    <div style={OUTER_WRAP}>
      <div style={COLS}>
        {/* LEFT — Workflow Details (read-only) */}
        <aside style={CARD}>
          <div style={CARD_HEADER}>
            <h2 style={CARD_HEADER_TITLE}>{t('workflow.details.infoTitle')}</h2>
          </div>
          <div style={CARD_BODY}>
            <div style={FIELD_BLOCK}>
              <span style={FIELD_LABEL}>{t('workflow.details.fieldName')}</span>
              <span style={FIELD_VALUE}>{emptyValue(name, notProvided)}</span>
            </div>
            <div style={FIELD_BLOCK}>
              <span style={FIELD_LABEL}>{t('workflow.details.fieldDescription')}</span>
              <span style={FIELD_VALUE}>{emptyValue(description, notProvided)}</span>
            </div>
            <div style={INLINE_ROW}>
              <span style={INLINE_LABEL}>{t('workflow.details.assignedTo')}:</span>
              {assignee}
            </div>
            <div style={INLINE_ROW}>
              <span style={INLINE_LABEL}>{t('workflow.fields.platform')}:</span>
              {platform}
            </div>
            {/* Dealer-code gating lists are optional and structurally absent for most
                workflows — hidden entirely when empty rather than showing "Not provided".
                Always sourced from the live template (see prop doc comment above), so a
                caveat is shown alongside a frozen historical version. */}
            {includedDealerCodes && includedDealerCodes.length > 0 && (
              <div style={FIELD_BLOCK}>
                <span style={FIELD_LABEL}>{t('workflow.details.includedDealerCodes')}</span>
                <span style={FIELD_VALUE}>
                  {includedDealerCodes.map((code) => (
                    <Tag key={code}>{code}</Tag>
                  ))}
                </span>
                {isFrozenSnapshot && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('workflow.details.dealerCodesLiveNote')}
                  </Text>
                )}
              </div>
            )}
            {excludedDealerCodes && excludedDealerCodes.length > 0 && (
              <div style={FIELD_BLOCK}>
                <span style={FIELD_LABEL}>{t('workflow.details.excludedDealerCodes')}</span>
                <span style={FIELD_VALUE}>
                  {excludedDealerCodes.map((code) => (
                    <Tag key={code}>{code}</Tag>
                  ))}
                </span>
                {isFrozenSnapshot && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('workflow.details.dealerCodesLiveNote')}
                  </Text>
                )}
              </div>
            )}
            {/* Version above Status so the pair reads as "here's the version
                we're looking at, and here's how it fared". */}
            {versionLabel && (
              <div style={INLINE_ROW}>
                <span style={INLINE_LABEL}>{t('workflow.fields.version')}:</span>
                {versionLabel}
              </div>
            )}
            <div style={INLINE_ROW}>
              <span style={INLINE_LABEL}>{t('workflow.fields.status')}:</span>
              <StatusTag status={status} isActive={isActive} decisionMode={statusDecisionMode} />
            </div>
            {/* Rejection reason inline — surfaces the approver's comment right
                where the Rejected badge lives, so nobody has to click into a
                modal to see WHY the workflow was rejected. */}
            {status === 'Rejected' && rejectionComment && (
              <div
                style={{
                  marginTop: 4,
                  padding: '10px 12px',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderLeft: '3px solid #dc2626',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#111827',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#b91c1c',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                    marginBottom: 4,
                  }}
                >
                  {t('workflow.list.rejectionComment')}
                </div>
                {rejectionComment}
              </div>
            )}
          </div>
        </aside>

        {/* RIGHT — Workflow Steps. Read-only list of every step; each row has a
            Preview button that opens the runtime form shell at that specific
            step so the reviewer can see what advisors will experience. */}
        <section style={CARD}>
          <div style={CARD_HEADER}>
            <h2 style={CARD_HEADER_TITLE}>{t('workflow.details.stepsTitle')}</h2>
            {steps.length > 0 && (
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  if (onPreview) {
                    onPreview();
                    return;
                  }
                  navigate(
                    versionNumber !== null
                      ? `${previewBase}/${template.id}/preview?version=${versionNumber}`
                      : `${previewBase}/${template.id}/preview`,
                  );
                }}
              >
                {t('workflow.builder.preview')}
              </Button>
            )}
          </div>
          <div style={{ ...CARD_BODY, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {steps.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6b7280',
                  fontSize: 14,
                  padding: '40px 0',
                }}
              >
                {t('workflow.details.stepsEmpty')}
              </div>
            ) : (
              steps.map((step, idx) => (
                <ReviewStepRow
                  key={step.id}
                  step={step}
                  index={idx + 1}
                  isFr={isFr}
                  template={template}
                  versionNumber={versionNumber}
                  previewBase={previewBase}
                  onPreviewPdfStep={setPdfPreviewStep}
                  onViewConfig={handleViewConfig}
                />
              ))
            )}
          </div>
        </section>
      </div>

      {/* Read-only PDF Documents drawer — opens when the reviewer clicks the eye
          on a PdfFileUpload step. Reuses the originator drawer with
          editable=false so it can only preview, not mutate. */}
      <PdfEntriesDrawer
        templateId={template.id}
        step={pdfPreviewStep}
        allSteps={steps}
        editable={false}
        onClose={() => setPdfPreviewStep(null)}
        onChanged={async () => {
          // No-op — drawer is read-only here, but the prop is required.
        }}
      />

      {/* Read-only E-signature config drawer — opens from "View configuration"
          on a OneSpanESign step. Reuses the originator drawer with
          editable=false so it can only preview, not mutate. */}
      <OneSpanConfigDrawer
        templateId={template.id}
        workflow={template}
        step={oneSpanConfigStep}
        editable={false}
        onClose={() => setOneSpanConfigStep(null)}
        onChanged={async () => {
          // No-op — drawer is read-only here, but the prop is required.
        }}
      />

      {/* Read-only Additional Documents config drawer — opens from "View
          configuration" on an AdditionalDocuments step. Reuses the originator
          drawer with editable=false so it can only preview, not mutate. */}
      <AdditionalDocumentEntriesDrawer
        templateId={template.id}
        step={docsConfigStep}
        allSteps={steps}
        editable={false}
        onClose={() => setDocsConfigStep(null)}
        onChanged={async () => {
          // No-op — drawer is read-only here, but the prop is required.
        }}
      />
    </div>
  );
}

/**
 * One review row per workflow step: numbered chip + type tag + bilingual title
 * + a Preview eye icon that opens the runtime form shell at that exact step.
 * Read-only — the reviewer can't reorder or edit anything from here.
 */
function ReviewStepRow({
  step,
  index,
  isFr,
  template,
  versionNumber,
  previewBase,
  onPreviewPdfStep,
  onViewConfig,
}: {
  step: WorkflowStep;
  index: number;
  isFr: boolean;
  template: WorkflowTemplate;
  /** Version row being viewed — threaded into the per-step preview URL so the
   *  preview shows THIS version's frozen step content rather than the
   *  template's current state. */
  versionNumber: number | null;
  previewBase: PreviewBase;
  /** Open the read-only PDF Documents drawer for a PdfFileUpload step. */
  onPreviewPdfStep: (step: WorkflowStep) => void;
  /** Open the read-only field/section configuration drawer for this step. */
  onViewConfig: (step: WorkflowStep) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const templateId = template.id;
  const title = (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr;
  const description =
    (isFr ? step.descriptionFr : step.descriptionEn) || step.descriptionEn || step.descriptionFr;
  const meta = metaFor(step.type);
  // Condition peek — sum every condition attached to this step OR to any of its
  // sections, so reviewers can see AT A GLANCE whether a step is gated by
  // branching logic; one click into the Popover names each rule in
  // human-readable form with the source field's authored label.
  const conditionEntries = collectStepConditions(step, template.steps ?? [], isFr);
  const hasConditions = conditionEntries.length > 0;
  // Per-type preview behaviour:
  //   DynamicForm / ApplicationReview / OneSpanESign / AdditionalDocuments
  //     → runtime form shell at this step.
  //   PdfGeneration → read-only PDF Documents drawer listing the configured
  //     templates, or no preview at all when none are configured yet.
  //
  // Before the 2026-08-08 merge these were two steps: PdfFileUpload owned the
  // templates and got the drawer, PdfGeneration owned the (not-yet-existing)
  // output and got nothing. Merged, the single step has templates worth
  // reviewing — so it gains the preview PdfGeneration used to be denied.
  const isPdfStep = step.type === 'PdfGeneration' || step.type === 'PdfFileUpload';
  const isPdfTemplates = isPdfStep && (step.fileEntries?.length ?? 0) > 0;
  const hasPreview = !isPdfStep || isPdfTemplates;
  // Steps with authored sections (DynamicForm + custom steps) expose the
  // read-only field/section config page; OneSpanESign / AdditionalDocuments
  // expose their type-specific read-only config drawer instead.
  const hasSections = (step.sections?.length ?? 0) > 0;
  const hasConfig =
    hasSections || step.type === 'OneSpanESign' || step.type === 'AdditionalDocuments';
  const handlePreview = () => {
    if (isPdfTemplates) {
      onPreviewPdfStep(step);
      return;
    }
    const versionQuery = versionNumber !== null ? `&version=${versionNumber}` : '';
    navigate(`${previewBase}/${templateId}/preview?stepId=${step.id}${versionQuery}`);
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        border: '1px solid #e5e7eb',
        borderRadius: 10,
        background: '#fff',
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: '#111827',
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {index}
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Text strong style={{ fontSize: 14, color: '#111827' }}>
          {title}
        </Text>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Tag
            style={{
              marginInlineEnd: 0,
              background: meta.tintBg,
              color: meta.tintFg,
              border: `1px solid ${meta.tintFg}33`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
            icon={meta.icon()}
          >
            {stepTypeLabel(step.type, t)}
          </Tag>
          {description && (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {description}
            </Text>
          )}
        </div>
      </div>
      {hasConditions && (
        <Popover
          trigger={['hover', 'click']}
          title={t('approver.review.conditionsPeekTitle', {
            defaultValue: 'Visibility conditions',
          })}
          content={
            <div style={{ maxWidth: 320, fontSize: 13, color: '#111827' }}>
              <ul style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 6 }}>
                {conditionEntries.map((c, i) => (
                  <li key={i}>
                    <Text style={{ fontSize: 13 }}>
                      <Text strong>{c.scope}</Text> — {c.summary}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          }
        >
          <Tag
            icon={<BranchesOutlined />}
            style={{
              marginInlineEnd: 0,
              cursor: 'help',
              background: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
            }}
          >
            {t('approver.review.conditionsPeekChip', {
              defaultValue: '{{count}} rule',
              count: conditionEntries.length,
              defaultValue_other: '{{count}} rules',
            })}
          </Tag>
        </Popover>
      )}
      {hasConfig && (
        <Tooltip title={t('workflow.review.config.viewConfiguration')}>
          <Button
            {...ICON_BTN_PROPS}
            icon={<ProfileOutlined />}
            onClick={() => onViewConfig(step)}
          />
        </Tooltip>
      )}
      {hasPreview && (
        <Tooltip
          title={t(
            isPdfTemplates ? 'approver.review.previewPdfTemplates' : 'approver.review.previewStep',
          )}
        >
          <Button {...ICON_BTN_PROPS} icon={<EyeOutlined />} onClick={handlePreview} />
        </Tooltip>
      )}
    </div>
  );
}
